/**
 * Agent Overview Dashboard
 * Shows agent-specific stats and quick actions
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './AgentOverview.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

const AgentOverview = ({ onTabChange }) => {
  const { t } = useTranslation('navigation');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    paymentsSubmittedToday: 0,
    pendingPayments: 0,
    approvedPaymentsToday: 0,
    rejectedPaymentsToday: 0
  });
  const [recentPayments, setRecentPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const isMountedRef = useRef(true);
  const hasLoadedOnceRef = useRef(false);

  // Get current user
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Only use name, never email
  const agentName = user.first_name && user.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : user.first_name || user.last_name || 'Agent';

  useEffect(() => {
    isMountedRef.current = true;
    // If we've loaded before, don't show loading state (preserve stats)
    if (hasLoadedOnceRef.current) {
      setLoading(false);
    }
    fetchAgentStats();
    fetchRecentPayments();
    fetchNotifications();
    
    // Refresh stats every 30 seconds
    const interval = setInterval(() => {
      if (isMountedRef.current) {
        fetchAgentStats();
        fetchRecentPayments();
      }
    }, 30000);
    
    // Listen for payment updates
    const handlePaymentUpdate = () => {
      if (isMountedRef.current) {
        fetchAgentStats();
        fetchRecentPayments();
      }
    };
    
    window.addEventListener('paymentUpdated', handlePaymentUpdate);
    
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener('paymentUpdated', handlePaymentUpdate);
    };
  }, []);

  const fetchAgentStats = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/agent/dashboard/stats`;
      console.log('Fetching agent stats from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agent stats response:', data);
        if (data.success && data.stats) {
          // Only update state if component is still mounted
          if (isMountedRef.current) {
            setStats(data.stats);
            setLoading(false);
            hasLoadedOnceRef.current = true;
          }
        } else {
          console.warn('Stats response missing success or stats:', data);
          if (isMountedRef.current) setLoading(false);
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to fetch agent stats:', response.status, errorData);
        if (isMountedRef.current) setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching agent stats:', error);
      if (isMountedRef.current) setLoading(false);
    }
  };

  const fetchRecentPayments = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/manual-payments/?status=all&limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && isMountedRef.current) {
          setRecentPayments(data.data.slice(0, 5));
        }
      }
    } catch (error) {
      console.error('Error fetching recent payments:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/notifications/agent`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.notifications && isMountedRef.current) {
          setNotifications(data.notifications.slice(0, 5));
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'status-badge pending';
      case 'approved':
        return 'status-badge approved';
      case 'rejected':
        return 'status-badge rejected';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="agent-overview">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="agent-overview">
      {/* Header */}
      <div className="overview-header">
        <div className="header-left">
          <h2>Welcome back, {agentName}</h2>
          <p className="role-badge">Agent Dashboard</p>
        </div>
        <div className="header-right">
          <div className="shift-status">
            <span className="status-indicator active"></span>
            <span>Active Shift</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon payments-submitted">
            <span className="material-icons">payment</span>
          </div>
          <div className="stat-content">
            <h3>{stats.paymentsSubmittedToday}</h3>
            <p>Payments Submitted Today</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon pending">
            <span className="material-icons">schedule</span>
          </div>
          <div className="stat-content">
            <h3>{stats.pendingPayments}</h3>
            <p>Pending Payments</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon approved">
            <span className="material-icons">check_circle</span>
          </div>
          <div className="stat-content">
            <h3>{stats.approvedPaymentsToday}</h3>
            <p>Approved Today</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon rejected">
            <span className="material-icons">cancel</span>
          </div>
          <div className="stat-content">
            <h3>{stats.rejectedPaymentsToday}</h3>
            <p>Rejected Today</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="overview-content-grid">
        {/* Recent Payments */}
        <div className="content-card">
          <div className="card-header">
            <h3>Recent Payment Requests</h3>
            <button 
              className="view-all-link"
              onClick={() => {
                if (onTabChange) {
                  onTabChange('pricing-billing');
                  setTimeout(() => {
                    const event = new CustomEvent('switchToPaymentsTab', { detail: 'manual-payments' });
                    window.dispatchEvent(event);
                  }, 300);
                }
              }}
            >
              View All <span className="material-icons">arrow_forward</span>
            </button>
          </div>
          <div className="card-body">
            {recentPayments.length === 0 ? (
              <div className="empty-state">
                <span className="material-icons empty-icon">receipt_long</span>
                <p className="empty-message">No payment requests yet</p>
                <button 
                  className="primary-btn small"
                  onClick={() => {
                    if (onTabChange) {
                      onTabChange('pricing-billing');
                      setTimeout(() => {
                        const event = new CustomEvent('openPaymentForm');
                        window.dispatchEvent(event);
                      }, 300);
                    }
                  }}
                >
                  Create First Payment
                </button>
              </div>
            ) : (
              <div className="payments-list">
                {recentPayments.map(payment => {
                  const customerName = payment.customer_first_name && payment.customer_last_name
                    ? `${payment.customer_first_name} ${payment.customer_last_name}`
                    : payment.customer_email || 'Unknown Customer';
                  
                  return (
                    <div key={payment.id} className="payment-item">
                      <div className="payment-info">
                        <div className="payment-customer">{customerName}</div>
                        <div className="payment-details">
                          <span>{payment.amount} {payment.currency || 'DA'}</span>
                          <span className="separator">•</span>
                          <span>{formatDate(payment.created_at)}</span>
                        </div>
                      </div>
                      <span className={getStatusBadgeClass(payment.status)}>
                        {payment.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="content-card">
          <div className="card-header">
            <h3>Recent Notifications</h3>
            <button 
              className="view-all-link"
              onClick={() => {
                if (onTabChange) {
                  onTabChange('notifications-alerts');
                }
              }}
            >
              View All <span className="material-icons">arrow_forward</span>
            </button>
          </div>
          <div className="card-body">
            {notifications.length === 0 ? (
              <div className="empty-state">
                <span className="material-icons empty-icon">notifications_none</span>
                <p className="empty-message">No notifications</p>
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map(notification => (
                  <div key={notification.id} className="notification-item">
                    <div className="notification-icon">
                      <span className="material-icons">
                        {notification.type === 'approved' ? 'check_circle' : 
                         notification.type === 'rejected' ? 'cancel' : 
                         'info'}
                      </span>
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{formatDate(notification.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button 
            className="action-card" 
            onClick={() => {
              if (onTabChange) {
                onTabChange('pricing-billing');
                // Open payment form after a short delay
                setTimeout(() => {
                  const event = new CustomEvent('openPaymentForm');
                  window.dispatchEvent(event);
                }, 300);
              }
            }}
          >
            <span className="material-icons">add_circle</span>
            <span>Create Payment Request</span>
          </button>
          <button 
            className="action-card"
            onClick={() => {
              if (onTabChange) {
                onTabChange('pricing-billing');
                // Switch to manual-payments tab after navigation
                setTimeout(() => {
                  const event = new CustomEvent('switchToPaymentsTab', { detail: 'manual-payments' });
                  window.dispatchEvent(event);
                }, 300);
              }
            }}
          >
            <span className="material-icons">receipt</span>
            <span>View My Payments</span>
          </button>
          <button 
            className="action-card"
            onClick={() => {
              if (onTabChange) {
                onTabChange('pricing-billing');
                // Switch to receipts tab after navigation
                setTimeout(() => {
                  const event = new CustomEvent('switchToPaymentsTab', { detail: 'receipts' });
                  window.dispatchEvent(event);
                }, 300);
              }
            }}
          >
            <span className="material-icons">print</span>
            <span>Print Receipts</span>
          </button>
          <button 
            className="action-card"
            onClick={() => {
              if (onTabChange) {
                onTabChange('audit-logs');
              }
            }}
          >
            <span className="material-icons">history</span>
            <span>View Activity Log</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentOverview;
